import React, { ReactElement, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { DashboardSetting } from '@Apis/companyDashboard';

export interface FilterOption {
    id: number;
    name: string;
}

export interface FilterGroupConfig {
    field: keyof DashboardSetting;
    label: string;
    options?: FilterOption[];
    type?: 'checkbox' | 'milestone';
}

interface FilterDialogProps {
    title: string;
    visible: boolean;
    onHide: () => void;
    groups: FilterGroupConfig[];
    draft: DashboardSetting;
    onChange: (field: keyof DashboardSetting, value: number[] | boolean) => void;
    onClear: () => void;
    onApply: () => void;
}

const getSelectedCount = (group: FilterGroupConfig, draft: DashboardSetting): number => {
    if (group.type === 'milestone') {
        return draft.isMilestonesOnly === true ? 1 : 0;
    }
    const value = draft[group.field];
    return Array.isArray(value) ? value.length : 0;
};

const getSelectedLabel = (group: FilterGroupConfig, draft: DashboardSetting): string => {
    if (group.type === 'milestone') {
        return draft.isMilestonesOnly === true ? '1' : 'All';
    }
    const count = getSelectedCount(group, draft);
    const total = group.options?.length ?? 0;
    if (count === 0 || count === total) return 'All';
    return String(count);
};

interface FilterGroupPanelProps {
    group: FilterGroupConfig;
    draft: DashboardSetting;
    onChange: (field: keyof DashboardSetting, value: number[] | boolean) => void;
}

const FilterGroupPanel = ({ group, draft, onChange }: FilterGroupPanelProps): ReactElement => {
    const [search, setSearch] = useState('');

    if (group.type === 'milestone') {
        return (
            <div className='filter-group-panel filter-group-panel--milestone'>
                <div className='filter-radio-row'>
                    <RadioButton
                        inputId={`${group.field}-only`}
                        name={String(group.field)}
                        checked={draft.isMilestonesOnly === true}
                        onChange={() => onChange('isMilestonesOnly', true)}
                    />
                    <label htmlFor={`${group.field}-only`}>Only Milestones</label>
                </div>
                <div className='filter-radio-row'>
                    <RadioButton
                        inputId={`${group.field}-all`}
                        name={String(group.field)}
                        checked={draft.isMilestonesOnly !== true}
                        onChange={() => onChange('isMilestonesOnly', false)}
                    />
                    <label htmlFor={`${group.field}-all`}>All Tasks and Milestones</label>
                </div>
            </div>
        );
    }

    const options = group.options ?? [];
    const searchTerm = search.trim().toLowerCase();
    const filteredOptions = searchTerm ? options.filter((option) => option.name.toLowerCase().includes(searchTerm)) : options;
    const selectedIds = (draft[group.field] as number[] | undefined) ?? [];

    const toggleOption = (id: number) => {
        const next = selectedIds.includes(id) ? selectedIds.filter((selectedId) => selectedId !== id) : [...selectedIds, id];
        onChange(group.field, next);
    };

    return (
        <div className='filter-group-panel'>
            <InputText
                className='filter-group-search'
                placeholder={`Search ${group.label}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div className='filter-group-options'>
                {filteredOptions.map((option) => {
                    const inputId = `${String(group.field)}-${option.id}`;
                    return (
                        <div className='filter-checkbox-row' key={option.id}>
                            <Checkbox
                                inputId={inputId}
                                value={option.id}
                                checked={selectedIds.includes(option.id)}
                                onChange={() => toggleOption(option.id)}
                            />
                            <label htmlFor={inputId}>{option.name}</label>
                        </div>
                    );
                })}
                {filteredOptions.length === 0 && <div className='filter-group-empty'>No options found</div>}
            </div>
        </div>
    );
};

const FilterDialog = ({ title, visible, onHide, groups, draft, onChange, onClear, onApply }: FilterDialogProps): ReactElement => {
    const footer = (
        <div className='filter-dialog-footer'>
            <button type='button' className='filter-dialog-clear' onClick={onClear}>
                Clear
            </button>
            <button type='button' className='filter-dialog-apply' onClick={onApply}>
                Apply Filters
            </button>
        </div>
    );

    return (
        <Dialog
            className='overview-filter-dialog'
            modal
            closable
            header={title}
            visible={visible}
            style={{ width: '520px' }}
            onHide={onHide}
            footer={footer}>
            <Accordion multiple>
                {groups.map((group) => (
                    <AccordionTab key={String(group.field)} header={`${group.label} (${getSelectedLabel(group, draft)})`}>
                        <FilterGroupPanel group={group} draft={draft} onChange={onChange} />
                    </AccordionTab>
                ))}
            </Accordion>
        </Dialog>
    );
};

export default FilterDialog;
